// How long ago, and a session's state words, the same on every platform (spec "Time and
// status"; proto/state.go; the shared cases are internal/agent/testdata/rows.json "ago",
// "state" and "clock"). Times are the machine's: ages count on its clock, as its lists last
// said it, whatever this computer's says. useNow keeps them counting while nothing else changes.

import { useEffect, useState } from 'react';
import type { Session } from '../net/protocol';

/** How long ago, short: "now" under a minute (or ahead: another clock), "6m", "3h", "2d". */
export function ago(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** `ago` as a phrase: "just now", "6m ago". */
export function agoPhrase(seconds: number): string {
  const a = ago(seconds);
  return a === 'now' ? 'just now' : `${a} ago`;
}

/** Seconds from iso (a machine time) to now (the machine's clock, ms); NaN when unknown. */
export function secondsSince(iso: string | undefined, now: number): number {
  const t = iso ? Date.parse(iso) : NaN;
  return (now - t) / 1000;
}

// The machine's clock as it last said it, and this computer's time then.
let machineSaid = NaN;
let deviceAtList = NaN;

/** A list came with the machine's clock (its `now`). */
export function heardMachineClock(now: string | undefined) {
  const t = now ? Date.parse(now) : NaN;
  if (!Number.isFinite(t)) return;
  machineSaid = t;
  deviceAtList = Date.now();
}

/** The machine's clock at device time `device` (ms): a difference under 2 s is the list's
 *  trip, not a clock that's off (proto.MachineNow). */
export function machineNowAt(device: number, said = machineSaid, at = deviceAtList): number {
  if (!Number.isFinite(said) || !Number.isFinite(at)) return device;
  const ahead = said - at;
  return Math.abs(ahead) < 2000 ? device : device + ahead;
}

export const machineNow = () => machineNowAt(Date.now());

/** A session's state words and the time they count from, as the machine says them; a
 *  machine from before says only the status. */
export function stateWords(s: Pick<Session, 'status' | 'exitCode' | 'lastActivity' | 'state'>): { label: string; since?: string } {
  if (s.state?.label) return s.state;
  switch (s.status) {
    case 'working':
      return { label: 'Running' };
    case 'waiting':
      return { label: 'Needs you' };
    case 'exited':
      return { label: s.exitCode != null ? `Exit ${s.exitCode}` : 'Exited' };
    case 'done':
      return { label: 'Done', since: s.lastActivity };
  }
  return { label: 'Idle', since: s.lastActivity };
}

/** The state as a row says it at now (ms, the machine's clock): "Running", "Needs you · 5m",
 *  "Done · 2h", "Idle · 3d", "Exit 1". */
export function stateText(st: { label: string; since?: string }, now: number): string {
  const secs = secondsSince(st.since, now);
  return Number.isFinite(secs) ? `${st.label} · ${ago(secs)}` : st.label;
}

/** The machine's clock, again every `every` ms (on its boundaries) and whenever the page is
 *  shown again: ages keep counting with nothing else changing, at most `every` late. */
export function useNow(every = 10_000): number {
  const [now, setNow] = useState(machineNow);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      setNow(machineNow());
      timer = setTimeout(tick, every - (Date.now() % every) + 50);
    };
    timer = setTimeout(tick, every - (Date.now() % every) + 50);
    const shown = () => document.visibilityState === 'visible' && setNow(machineNow());
    document.addEventListener('visibilitychange', shown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', shown);
    };
  }, [every]);
  return now;
}
